export type ClassNameModifiers = Record<string, boolean | string | number | null | undefined>;

type ClassNameElementArg = string | ClassNameModifiers;
type ClassNameModifiersArg = string | number | ClassNameModifiers;

export type ClassNameFn = (element?: ClassNameElementArg, modifiers?: ClassNameModifiersArg) => { className: string };

function bem(blockName: string, element: string | undefined, modifiers: ClassNameModifiers): string {
  if (!element) return blockName;

  const base = `${blockName}__${element}`;

  return Object.keys(modifiers).reduce((result, name) => {
    const value = modifiers[name];
    if (!value) return result;
    return `${result} ${typeof value === 'boolean' ? `${base}--${name}` : `${base}--${name}_${value}`}`;
  }, base);
}

function resolveClassNameArgs(
  element: ClassNameElementArg,
  modifiers: ClassNameModifiersArg,
): { element: string | undefined; modifiers: ClassNameModifiers } {
  const resolved: { element: string | undefined; modifiers: ClassNameModifiers } = {
    element: typeof element === 'object' ? undefined : element,
    modifiers: typeof modifiers === 'object' ? modifiers : {},
  };

  if (typeof modifiers === 'string' || typeof modifiers === 'number') {
    resolved.modifiers = modifiers.toString().trim().length === 0 ? {} : { [modifiers]: true };
  }

  if (typeof element === 'object') {
    resolved.element = 'wrapper';
    resolved.modifiers = element;
  }

  return resolved;
}

/**
 * @deprecated Local port of the removed `@travpro/hooks` package, kept only for its existing
 * call sites. Do not use in new code — style with SCSS modules (`_component.module.scss`
 * imported as `c`, referenced as `c.className`) per CLAUDE.md instead of building BEM class
 * strings at runtime.
 */
export function useClassName(blockName: string): ClassNameFn {
  return (element: ClassNameElementArg = 'wrapper', modifiers: ClassNameModifiersArg = {}) => {
    const resolved = resolveClassNameArgs(element, modifiers);
    return { className: bem(blockName, resolved.element, resolved.modifiers) };
  };
}
